import { Search, ChevronDown, Filter, Columns3, SlidersHorizontal, X, Paperclip, FileText, MoreVertical, Pencil, Trash2, Plus, LayoutTemplate, Users, ArrowUp, ArrowDown, AlertTriangle, Archive, ArchiveRestore, StickyNote, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useDarkMode } from '../context/DarkModeContext';
import { api } from '../lib/api';
import { getRemainingTimeBadge, remainingBadgeClasses } from '../lib/remainingTime';
import { PlanLimitModal } from '../components/PlanLimitModal';
import ConfirmDialog from '../components/admin/ConfirmDialog';
import { HilfeLink } from '../components/HilfeLink';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

// Ein-Klick-Bestätigung "liegt bei BAFA, von uns hängt nichts mehr ab" (siehe Dashboard.tsx/
// VorgangAccordion.tsx) — ein normaler Berichts-Status, kein eigenes Datenbankfeld.
const STATUS_BEI_BAFA = 'Bei BAFA zur Prüfung';

interface ApiReport {
  id: number;
  berichtstitel: string;
  aktenzeichen: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  notiz?: string | null;
  fortschritt: number;
  customerId: number;
  customer: { id: number; name: string } | null;
  files?: unknown[];
  sections?: Array<{ sectionKey: string; finalText?: string | null; content?: string | null }>;
  consultingStart?: string | null;
  consultingEnd?: string | null;
  totalConsultingHours?: number;
  hasDayConflict?: boolean;
  updatedAt: string;
  createdAt: string;
  isShared?: boolean;
  sharedBy?: number;
  creatorName?: string;
  archived?: boolean;
}

// Sections that count toward "Bericht fertig" — must mirror EditorWorkspace.
// `cards` = expected sub-cards in multi-card sections; for single sections the
// whole section finalText counts as one unit.
const EXPECTED_SECTIONS: Array<{ key: string; cards?: string[] }> = [
  { key: 'section1', cards: ['card-1-1', 'card-1-2', 'card-1-3', 'card-1-4', 'card-1-5'] },
  { key: 'analysis', cards: ['card-2-1', 'card-2-2', 'card-2-3'] },
  { key: 'execution' },
  { key: 'weaknesses', cards: ['card-4-1', 'card-4-2'] },
  { key: 'action-plan' },
  { key: 'principles' },
  { key: 'confirmation' },
];

function stripHtml(html?: string | null): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

function sectionFillPercent(
  sec: { sectionKey: string; finalText?: string | null; content?: string | null } | undefined,
  cards: string[] | undefined,
): number {
  if (!sec) return 0;
  // Multi-card section: percentage = filled cards / expected cards.
  if (cards && cards.length > 0) {
    let parsed: any = null;
    if (sec.content) {
      try { parsed = JSON.parse(sec.content); } catch { /* noop */ }
    }
    const cardTexts = parsed?.cardTexts;
    if (!cardTexts || typeof cardTexts !== 'object') return 0;
    const filled = cards.filter((id) => stripHtml(cardTexts[id]?.finalText).length > 0).length;
    return (filled / cards.length) * 100;
  }
  // Single-section: 100 if its finalText has real content, else 0.
  if (stripHtml(sec.finalText).length > 0) return 100;
  // 'principles'/'confirmation' koennen ohne Text als erledigt gelten (z.B. wenn
  // Grundsätze bewusst ausgeschlossen wurde) — Editor zaehlt das ebenfalls als
  // fertig, siehe getSectionProgress in EditorWorkspace.tsx. Ohne diesen Fallback
  // zieht ein ausgeschlossener Abschnitt die Quote dauerhaft unter 100%, obwohl
  // der Editor den Bericht als vollstaendig gruen anzeigt.
  if (sec.sectionKey === 'principles' || sec.sectionKey === 'confirmation') {
    let parsed: any = null;
    if (sec.content) {
      try { parsed = JSON.parse(sec.content); } catch { /* noop */ }
    }
    if (parsed?.confirmed === true) return 100;
  }
  return 0;
}

export function computeReportProgress(sections?: ApiReport['sections']): number {
  if (!sections || sections.length === 0) return 0;
  const total = EXPECTED_SECTIONS.reduce((sum, def) => {
    const sec = sections.find((s) => s.sectionKey === def.key);
    return sum + sectionFillPercent(sec, def.cards);
  }, 0);
  return Math.round(total / EXPECTED_SECTIONS.length);
}

interface MappedReport {
  id: number;
  kunde: string;
  berichtstitel: string;
  aktenzeichen: string;
  startDate: string;
  endDate: string;
  hasRealEndDate: boolean;
  fileCount: number;
  status: string;
  notiz: string | null;
  statusColor: string;
  fortschritt: number;
  zuletzt: string;
  zuletztDatum: string;
  zuletztZeit: string;
  consultingStart: string | null;
  consultingEnd: string | null;
  totalConsultingHours: number;
  hasDayConflict: boolean;
  archived: boolean;
}

function CircularProgress({ value, color }: { value: number; color: string }) {
  const r = 11;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(value, 100) / 100);
  const isComplete = value >= 100;
  const trackColor = color === 'green'
    ? 'stroke-green-200 dark:stroke-green-900'
    : 'stroke-slate-200 dark:stroke-slate-700';
  const arcColor = color === 'green'
    ? 'stroke-green-500'
    : value >= 60
    ? 'stroke-blue-500'
    : value >= 30
    ? 'stroke-orange-400'
    : 'stroke-slate-400';

  return (
    <div className="relative w-7 h-7 shrink-0" title={`${value}% ausgefüllt`}>
      <svg viewBox="0 0 28 28" className="w-7 h-7 -rotate-90">
        <circle cx="14" cy="14" r={r} strokeWidth="3" fill="none" className={trackColor} />
        <circle
          cx="14" cy="14" r={r} strokeWidth="3" fill="none"
          className={arcColor}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>
      <span className={`absolute inset-0 flex items-center justify-center text-[10px] font-bold ${
        isComplete ? 'text-green-600 dark:text-green-400' : 'text-slate-600 dark:text-slate-300'
      }`}>
        {isComplete ? '✓' : `${value}`}
      </span>
    </div>
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { status, refresh } = useSubscriptionStatus();
  const [limitModalCode, setLimitModalCode] = useState<'FREE_REPORT_LIMIT_REACHED' | 'MONTHLY_REPORT_LIMIT_REACHED' | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; title: string } | null>(null);
  // Status-Mehrfachfilter (Häkchen wie beim Spalten-Dropdown): `hiddenStatuses` = abgewählte
  // Status (leer = alle sichtbar, auch neu von der BAFA gelieferte). `showArchivedStatus` = Häkchen „Archiviert".
  // Beide werden — wie die übrigen Tabellen-Einstellungen — in localStorage gemerkt.
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem('reports.hiddenStatuses');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return new Set(parsed.filter((x: any): x is string => typeof x === 'string'));
      }
    } catch { /* ignore */ }
    return new Set();
  });
  const [showArchivedStatus, setShowArchivedStatus] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return localStorage.getItem('reports.showArchivedStatus') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('reports.hiddenStatuses', JSON.stringify([...hiddenStatuses])); } catch { /* ignore */ }
  }, [hiddenStatuses]);
  useEffect(() => {
    try { localStorage.setItem('reports.showArchivedStatus', String(showArchivedStatus)); } catch { /* ignore */ }
  }, [showArchivedStatus]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  // Unter 768px gibt es keine Kopfzeile zum Sortieren und keinen Platz für drei
  // Knöpfe — dort sammelt ein Vollbild-Dialog Suche, Sortierung, Status und Spalten.
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [offenerBereich, setOffenerBereich] = useState<'sortierung' | 'status' | 'spalten' | null>('sortierung');
  const [notizEdit, setNotizEdit] = useState<{ id: number; titel: string; value: string } | null>(null);
  const [notizSaving, setNotizSaving] = useState(false);
  const saveNotiz = async () => {
    if (!notizEdit) return;
    setNotizSaving(true);
    try {
      const val = notizEdit.value;
      await api.patch(`/api/reports/${notizEdit.id}`, { notiz: val });
      setAllReports(prev => prev.map(r => r.id === notizEdit.id ? { ...r, notiz: val } : r));
      setNotizEdit(null);
    } catch { /* Fehler: Modal offen lassen */ }
    finally { setNotizSaving(false); }
  };

  const performDeleteReport = async () => {
    if (!deleteConfirm) return;
    const { id } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await api.delete(`/api/reports/${id}`);
      setAllReports(prev => prev.filter(r => r.id !== id));
      await refresh();
    } catch { /* noop */ }
  };

  const isAtReportLimit = status
    ? status.reportUsage.used >= status.reportUsage.limit && status.reportUsage.credits === 0
    : false;

  const handleNewReport = () => {
    if (isAtReportLimit && status) {
      const code = status.reportUsage.mode === 'total'
        ? 'FREE_REPORT_LIMIT_REACHED'
        : 'MONTHLY_REPORT_LIMIT_REACHED';
      setLimitModalCode(code);
      return;
    }
    navigate('/editor');
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    if (typeof window === 'undefined') return 10;
    const stored = parseInt(localStorage.getItem('reports.rowsPerPage') || '', 10);
    return Number.isFinite(stored) && stored > 0 ? stored : 10;
  });
  useEffect(() => {
    try { localStorage.setItem('reports.rowsPerPage', String(rowsPerPage)); } catch { /* ignorieren */ }
  }, [rowsPerPage]);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [rowsDropdownOpen, setRowsDropdownOpen] = useState(false);
  // Reorderable main column keys (berichtstitel + kunde sind ein gemeinsamer Block 'titel'
  // mit zwei Sub-Toggles, die separat ein-/ausblendbar sind).
  const DEFAULT_REPORT_COLUMN_ORDER = ['fortschritt', 'vorgangsnummer', 'titel', 'notiz', 'zeitraum', 'zeitangaben', 'restzeit', 'status', 'zuletzt'];
  const DEFAULT_REPORT_VISIBLE = {
    vorgangsnummer: true,
    kunde: true,
    berichtstitel: true,
    notiz: true,
    zeitraum: true,
    zeitangaben: true,
    restzeit: true,
    fortschritt: true,
    status: true,
    zuletzt: true,
  };

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return DEFAULT_REPORT_VISIBLE;
    try {
      const raw = localStorage.getItem('reports.visibleColumns');
      if (raw) return { ...DEFAULT_REPORT_VISIBLE, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT_REPORT_VISIBLE;
  });

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (typeof window === 'undefined') return DEFAULT_REPORT_COLUMN_ORDER;
    try {
      const raw = localStorage.getItem('reports.columnOrder');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const known = parsed.filter((k: any) => DEFAULT_REPORT_COLUMN_ORDER.includes(k));
          const result = [...known];
          DEFAULT_REPORT_COLUMN_ORDER.forEach((k, i) => {
            if (result.includes(k)) return;
            const prev = DEFAULT_REPORT_COLUMN_ORDER[i - 1];
            const at = prev && result.includes(prev) ? result.indexOf(prev) + 1 : 0;
            result.splice(at, 0, k);
          });
          return result;
        }
      }
    } catch { /* ignore */ }
    return DEFAULT_REPORT_COLUMN_ORDER;
  });

  useEffect(() => {
    try { localStorage.setItem('reports.visibleColumns', JSON.stringify(visibleColumns)); } catch { /* ignore */ }
  }, [visibleColumns]);
  useEffect(() => {
    try { localStorage.setItem('reports.columnOrder', JSON.stringify(columnOrder)); } catch { /* ignore */ }
  }, [columnOrder]);

  const [dragKey, setDragKey] = useState<string | null>(null);
  const reorderTo = (from: string, to: string) => {
    if (from === to) return;
    setColumnOrder(order => {
      const fromIdx = order.indexOf(from);
      const toIdx = order.indexOf(to);
      if (fromIdx < 0 || toIdx < 0) return order;
      const next = [...order];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, from);
      return next;
    });
  };

  const orderLabels: Record<string, string> = {
    fortschritt: 'Stand',
    vorgangsnummer: 'Vorgang',
    titel: 'Bericht / Kunde',
    notiz: 'Notiz',
    zeitraum: 'Zeitraum',
    zeitangaben: 'Zeitangaben',
    restzeit: 'Restzeit',
    status: 'Status',
    zuletzt: 'Letzte Änderung',
  };

  /** Visibility-Status für eine reorderable Hauptspalte. */
  const isOrderColVisible = (key: string): boolean => {
    if (key === 'titel') return !!visibleColumns.berichtstitel || !!visibleColumns.kunde;
    return !!visibleColumns[key];
  };

  const columnWidth = (key: string): string => {
    switch (key) {
      case 'fortschritt': return '3rem';
      case 'vorgangsnummer': return '7rem';
      case 'titel': return '1fr';
      case 'notiz': return '12rem';
      case 'zeitraum': return '10rem';
      case 'zeitangaben': return '9rem';
      case 'restzeit': return '6.5rem';
      case 'status': return '10rem';
      case 'zuletzt': return '9rem';
      default: return 'auto';
    }
  };

  // Grid-Template — von Header und Datenzeilen via subgrid geerbt.
  const gridCols = useMemo(() => {
    const parts: string[] = [];
    for (const key of columnOrder) {
      if (!isOrderColVisible(key)) continue;
      parts.push(columnWidth(key));
    }
    parts.push('auto'); // Aktionen-Menü
    return parts.join(' ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnOrder, visibleColumns]);

  const SORT_OPTIONS: Array<{ key: string; label: string }> = [
    { key: 'restzeit', label: 'Restzeit (Tage)' },
    { key: 'vorgangsnummer', label: 'Vorgangsnummer' },
    { key: 'kunde', label: 'Kunde' },
    { key: 'berichtstitel', label: 'Berichtstitel' },
    { key: 'zeitraum', label: 'Zeitraum' },
    { key: 'zeitangaben', label: 'Zeitangaben (Beratung)' },
    { key: 'dateien', label: 'Dateien' },
    { key: 'status', label: 'Status' },
    { key: 'zuletzt', label: 'Zuletzt bearbeitet' },
  ];

  // Sortierung — vordefiniert nach Restzeit (wenigste verbleibende Tage zuerst),
  // zuletzt gewählte Sortierung wird in localStorage gemerkt.
  const [sortColumn, setSortColumn] = useState<string>(() => {
    if (typeof window === 'undefined') return 'restzeit';
    const stored = localStorage.getItem('reports.sortColumn');
    return stored && SORT_OPTIONS.some((o) => o.key === stored) ? stored : 'restzeit';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    if (typeof window === 'undefined') return 'asc';
    return localStorage.getItem('reports.sortDirection') === 'desc' ? 'desc' : 'asc';
  });
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const [menuBtnEl, setMenuBtnEl] = useState<HTMLButtonElement | null>(null);
  const closeMenuTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelMenuClose = () => { if (closeMenuTimer.current) { clearTimeout(closeMenuTimer.current); closeMenuTimer.current = null; } };
  const scheduleMenuClose = () => { cancelMenuClose(); closeMenuTimer.current = setTimeout(() => setOpenMenuId(null), 800); };
  const openMenuAt = (rowId: number, ev: React.MouseEvent<HTMLButtonElement>) => {
    cancelMenuClose();
    if (openMenuId === rowId) { setOpenMenuId(null); setMenuBtnEl(null); return; }
    const btn = ev.currentTarget;
    const rect = btn.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuBtnEl(btn);
    setOpenMenuId(rowId);
  };
  useEffect(() => {
    if (openMenuId === null || !menuBtnEl) return;
    const update = () => {
      const rect = menuBtnEl.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [openMenuId, menuBtnEl]);

  useEffect(() => {
    try {
      localStorage.setItem('reports.sortColumn', sortColumn);
      localStorage.setItem('reports.sortDirection', sortDirection);
    } catch { /* localStorage nicht verfügbar — ignorieren */ }
  }, [sortColumn, sortDirection]);
  const setSort = (key: string) => {
    if (key === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDirection('asc');
    }
  };

  const [allReports, setAllReports] = useState<MappedReport[]>([]);

  useEffect(() => {
    api.get<ApiReport[]>('/api/reports')
      .then((reports) => {
        const mapped = reports.map((r) => {
          const statusColor =
            r.status === 'Abgeschlossen' || r.status === 'Ausgezahlt' || r.status === 'Zahlung erfolgt' ? 'green' :
            r.status === 'Abgelehnt' ? 'red' :
            r.status === 'Neu' ? 'blue' : 'yellow';
          const updatedDate = new Date(r.updatedAt);
          const zuletzt = updatedDate.toLocaleDateString('de-DE', {
            day: '2-digit', month: '2-digit', year: '2-digit'
          });
          const zuletztDatum = zuletzt;
          const zuletztZeit = updatedDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
          return {
            id: r.id,
            kunde: r.customer?.name || '–',
            berichtstitel: r.berichtstitel,
            aktenzeichen: r.aktenzeichen || '–',
            startDate: r.startDate || r.createdAt,
            endDate: r.endDate || (() => { const d = new Date(r.createdAt); d.setMonth(d.getMonth() + 6); return d.toISOString(); })(),
            hasRealEndDate: !!r.endDate,
            fileCount: r.files?.filter((f: { category: string }) => f.category !== 'ESF Fragebogen').length || 0,
            status: r.status,
            notiz: r.notiz ?? null,
            statusColor,
            fortschritt: computeReportProgress(r.sections),
            zuletzt,
            zuletztDatum,
            zuletztZeit,
            consultingStart: r.consultingStart ?? null,
            consultingEnd: r.consultingEnd ?? null,
            totalConsultingHours: r.totalConsultingHours ?? 0,
            hasDayConflict: r.hasDayConflict ?? false,
            archived: r.archived ?? false,
          };
        });
        setAllReports(mapped);

        // Fallback: if the list endpoint didn't include sections (old backend),
        // fetch them per-report and recompute progress. Nur für wenige Berichte —
        // bei vielen sprengt das N+1 das Anfrage-Limit (429), danach öffnen sich
        // Berichte nicht mehr. Die Liste liefert die Abschnitte inzwischen mit.
        const missing = reports.filter((r) => !r.sections);
        if (missing.length > 0 && missing.length <= 10) {
          Promise.all(
            missing.map((r) =>
              api.get<ApiReport>(`/api/reports/${r.id}`)
                .then((full) => ({ id: r.id, fortschritt: computeReportProgress(full.sections) }))
                .catch(() => null),
            ),
          ).then((results) => {
            const updates = results.filter((x): x is { id: number; fortschritt: number } => !!x);
            if (updates.length === 0) return;
            setAllReports((prev) =>
              prev.map((row) => {
                const u = updates.find((x) => x.id === row.id);
                return u ? { ...row, fortschritt: u.fortschritt } : row;
              }),
            );
          });
        }

        // Live BAFA status refresh (non-blocking, updates status badges in place)
        api.post<{ id: number; status: string }[]>('/api/reports/refresh-bafa', {})
          .then((updates) => {
            if (!updates || updates.length === 0) return;
            setAllReports((prev) =>
              prev.map((row) => {
                const u = updates.find((x) => x.id === row.id);
                if (!u || u.status === row.status) return row;
                const statusColor =
                  u.status === 'Abgeschlossen' || u.status === 'Ausgezahlt' || u.status === 'Zahlung erfolgt' ? 'green' :
                  u.status === 'Abgelehnt' ? 'red' :
                  u.status === 'Neu' ? 'blue' : 'yellow';
                return { ...row, status: u.status, statusColor };
              }),
            );
          })
          .catch(() => {});
      })
      .catch(() => {});
  }, []);

  const filteredReports = allReports.filter((report) => {
    if (report.archived) {
      if (!showArchivedStatus) return false;
    } else {
      if (hiddenStatuses.has(report.status)) return false;
    }
    const query = searchQuery.toLowerCase();
    return (
      report.kunde.toLowerCase().includes(query) ||
      report.berichtstitel.toLowerCase().includes(query) ||
      report.aktenzeichen.toLowerCase().includes(query)
    );
  });

  const statusOptions = Array.from(new Set(allReports.filter(r => !r.archived).map(r => r.status).filter(Boolean))).sort();
  const isStatusChecked = (s: string) => !hiddenStatuses.has(s);
  const toggleStatus = (s: string) => setHiddenStatuses(prev => {
    const next = new Set(prev);
    if (next.has(s)) next.delete(s); else next.add(s);
    return next;
  });
  const allStatusChecked = statusOptions.every(isStatusChecked);
  const toggleAllStatus = () => setHiddenStatuses(allStatusChecked ? new Set(statusOptions) : new Set());
  const statusFilterActive = !allStatusChecked || showArchivedStatus;
  const checkedStatusCount = statusOptions.filter(isStatusChecked).length + (showArchivedStatus ? 1 : 0);

  const sortedReports = [...filteredReports].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortColumn) {
      case 'kunde': return dir * a.kunde.localeCompare(b.kunde);
      case 'berichtstitel': return dir * a.berichtstitel.localeCompare(b.berichtstitel);
      case 'vorgangsnummer': return dir * a.aktenzeichen.localeCompare(b.aktenzeichen);
      case 'zeitraum': return dir * (a.startDate.localeCompare(b.startDate));
      case 'zeitangaben': {
        // Nach dem ersten Beratungstag (Karte 1.5) sortieren; Berichte ohne
        // erfasste Beratungstage immer ans Ende, unabhängig von der Richtung.
        if (!a.consultingStart && !b.consultingStart) return 0;
        if (!a.consultingStart) return 1;
        if (!b.consultingStart) return -1;
        return dir * a.consultingStart.localeCompare(b.consultingStart);
      }
      case 'restzeit': {
        const aDone = a.status === 'Abgeschlossen';
        const bDone = b.status === 'Abgeschlossen';
        // Abgeschlossene Berichte immer ans Ende
        if (aDone && !bDone) return 1;
        if (!aDone && bDone) return -1;
        if (aDone && bDone) return 0;
        const daysA = Math.ceil((new Date(a.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        const daysB = Math.ceil((new Date(b.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
        return dir * (daysA - daysB);
      }
      case 'dateien': return dir * (a.fileCount - b.fileCount);
      case 'status': return dir * a.status.localeCompare(b.status);
      case 'zuletzt': return dir * a.zuletzt.localeCompare(b.zuletzt);
      default: return 0;
    }
  });

  const displayedReports = sortedReports.slice(0, rowsPerPage);

  const toggleColumn = (column: string) => {
    setVisibleColumns((prev) => ({ ...prev, [column]: !prev[column] }));
  };

  const toggleAllColumns = () => {
    const allVisible = Object.values(visibleColumns).every((v) => v);
    // Aus DEFAULT_REPORT_VISIBLE ableiten, damit ALLE Spalten (inkl. notiz, zeitangaben)
    // erfasst werden — eine manuelle Key-Liste ließ sonst Spalten aus dem State fallen.
    const newState = Object.fromEntries(
      Object.keys(DEFAULT_REPORT_VISIBLE).map((k) => [k, !allVisible]),
    );
    setVisibleColumns(newState);
  };

  const allColumnsVisible = Object.values(visibleColumns).every((v) => v);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  // Kompaktes „TT.MM." für die Zeitangaben-Spalte (Karte 1.5) — spart Platz ggü. formatDate.
  const fmtDayShort = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };
  // Stunden hübsch: ganze Zahl ohne Nachkomma, sonst eine Stelle (z.B. 32 h / 4,5 h).
  const fmtHours = (h: number) => (Number.isInteger(h) ? String(h) : h.toFixed(1).replace('.', ','));
  const renderZeitangaben = (r: MappedReport) => {
    const hasRange = r.consultingStart || r.consultingEnd;
    const hasHours = r.totalConsultingHours > 0;
    if (!hasRange && !hasHours) return <span className="text-slate-300 dark:text-slate-600">–</span>;
    return (
      <>
        {hasRange && (
          <span className="whitespace-nowrap">{fmtDayShort(r.consultingStart)}{r.consultingEnd && r.consultingEnd !== r.consultingStart ? ` – ${fmtDayShort(r.consultingEnd)}` : ''}</span>
        )}
        {hasHours && <span className="whitespace-nowrap">{hasRange ? ' / ' : ''}{fmtHours(r.totalConsultingHours)} h</span>}
      </>
    );
  };

  return (
    <div className="min-h-screen flex bg-slate-100 dark:bg-slate-900 overflow-x-hidden">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8">
          <div className="w-full max-w-full">

            {/* Kopfzeile — flach (ohne Kartenfläche und Innenabstand). */}
            <div className="mb-4">
              <div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-0.5">
                      Neuen Bericht erstellen
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Starten Sie einen neuen Beratungsbericht für einen Kunden.
                    </p>
                  </div>
                  <div className="flex items-start gap-2 flex-1 min-w-0 justify-end">
                  <button
                    onClick={() => setFilterSheetOpen(true)}
                    title="Filter"
                    aria-label="Filter"
                    className="md:hidden w-12 h-12 aspect-square flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm hover:shadow-md cursor-pointer relative shrink-0"
                  >
                    <SlidersHorizontal className="w-5 h-5" />
                    {(searchQuery.trim() !== '' || statusFilterActive) && (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />
                    )}
                  </button>
                  <div className="hidden md:flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <div className="relative flex-1 min-w-0 max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Kunde, Titel oder Vorgangsnummer..."
                        aria-label="Berichte durchsuchen"
                        className="w-full h-12 pl-9 pr-3 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-500"
                      />
                    </div>
                  {/* Status-Filter (Mehrfachauswahl mit Häkchen, wie beim Spalten-Dropdown) */}
                  <div className="relative shrink-0">
                      <button
                        onClick={() => { setStatusDropdownOpen(!statusDropdownOpen); setColumnsDropdownOpen(false); setRowsDropdownOpen(false); }}
                        title={statusFilterActive ? `Status-Filter aktiv (${checkedStatusCount} ausgewählt)` : 'Nach Status filtern'}
                        aria-label="Nach Status filtern"
                        className={`relative w-12 h-12 aspect-square flex items-center justify-center rounded-lg transition-colors cursor-pointer shadow-sm hover:shadow-md ${
                          statusDropdownOpen
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        <Filter className="w-5 h-5" />
                        {statusFilterActive && (
                          <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />
                        )}
                      </button>
                      {statusDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setStatusDropdownOpen(false)} />
                        <div className="absolute left-0 right-0 sm:left-auto sm:right-0 mt-2 sm:w-64 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                          <div className="p-2">
                            <label className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-600 rounded cursor-pointer border-b border-slate-200 dark:border-slate-600 mb-1">
                              <input type="checkbox" checked={allStatusChecked} onChange={toggleAllStatus} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                              <span className="text-sm font-medium text-slate-900 dark:text-white">Alle Status</span>
                            </label>
                            {statusOptions.map((s) => (
                              <label key={s} className="flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600">
                                <input type="checkbox" checked={isStatusChecked(s)} onChange={() => toggleStatus(s)} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                                <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">{s}</span>
                              </label>
                            ))}
                            <label className="flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600 border-t border-slate-200 dark:border-slate-600 mt-1 pt-2">
                              <input type="checkbox" checked={showArchivedStatus} onChange={() => setShowArchivedStatus(v => !v)} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                              <span className="text-sm text-slate-700 dark:text-slate-300">Archiviert</span>
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Column Visibility Dropdown */}
                  <div className="relative shrink-0">
                      <button
                        onClick={() => { setColumnsDropdownOpen(!columnsDropdownOpen); setStatusDropdownOpen(false); setRowsDropdownOpen(false); }}
                        title="Spalten wählen und sortieren"
                        aria-label="Spalten wählen und sortieren"
                        className={`w-12 h-12 aspect-square flex items-center justify-center rounded-lg transition-colors cursor-pointer shadow-sm hover:shadow-md ${
                          columnsDropdownOpen
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        <Columns3 className="w-5 h-5" />
                      </button>
                      {columnsDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setColumnsDropdownOpen(false)} />
                        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto">
                          <div className="p-2">
                            <label className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-600 rounded cursor-pointer border-b border-slate-200 dark:border-slate-600 mb-1">
                              <input type="checkbox" checked={allColumnsVisible} onChange={toggleAllColumns} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                              <span className="text-sm font-medium text-slate-900 dark:text-white">Alle Spalten</span>
                            </label>
                            <div className="text-[12px] uppercase tracking-wider text-slate-400 dark:text-slate-500 px-3 pt-1 pb-0.5">Ziehen zum Sortieren</div>
                            {columnOrder.map((col) => (
                              <div
                                key={col}
                                draggable
                                onDragStart={() => setDragKey(col)}
                                onDragEnd={() => setDragKey(null)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => { e.preventDefault(); if (dragKey) reorderTo(dragKey, col); setDragKey(null); }}
                                className={`flex items-center gap-2 px-3 py-2 rounded select-none ${dragKey === col ? 'opacity-50' : ''} ${dragKey && dragKey !== col ? 'hover:bg-blue-50 dark:hover:bg-blue-900/30 border border-dashed border-transparent hover:border-blue-400' : 'hover:bg-slate-50 dark:hover:bg-slate-600'}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isOrderColVisible(col)}
                                  onChange={() => {
                                    if (col === 'titel') {
                                      const next = !(visibleColumns.berichtstitel || visibleColumns.kunde);
                                      setVisibleColumns(p => ({ ...p, berichtstitel: next, kunde: next }));
                                    } else {
                                      toggleColumn(col);
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">{orderLabels[col]}</span>
                                <span className="cursor-grab active:cursor-grabbing text-slate-400" aria-hidden="true" title="Zum Verschieben ziehen">⋮⋮</span>
                              </div>
                            ))}
                            {/* Sub-Toggles für die kombinierte Titel-Spalte */}
                            <div className="border-t border-slate-200 dark:border-slate-600 mt-1 pt-2 px-1 text-[12px] uppercase tracking-wider text-slate-400 dark:text-slate-500">Unter „Bericht / Kunde"</div>
                            <label className="flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600">
                              <input type="checkbox" checked={!!visibleColumns.berichtstitel} onChange={() => toggleColumn('berichtstitel')} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                              <span className="text-sm text-slate-700 dark:text-slate-300">Berichtstitel</span>
                            </label>
                            <label className="flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600">
                              <input type="checkbox" checked={!!visibleColumns.kunde} onChange={() => toggleColumn('kunde')} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer" />
                              <span className="text-sm text-slate-700 dark:text-slate-300">Kunde</span>
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <HilfeLink anleitungId="bericht-schreiben" variant="toolbar" label="Anleitung: Bericht schreiben" />
                    <button
                      onClick={handleNewReport}
                      title="Neuer Bericht"
                      aria-label="Neuer Bericht"
                      className="w-12 h-12 aspect-square flex items-center justify-center bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => navigate('/templates')}
                      title="Vorlagen"
                      aria-label="Vorlagen"
                      className="w-12 h-12 aspect-square flex items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <LayoutTemplate className="w-5 h-5" />
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Summary Cards */}
            {(() => {
              const activeReports = allReports.filter(r => !r.archived);
              // "Bei BAFA zur Prüfung" (Ein-Klick-Knopf) ist explizit "nichts zu tun" — zählte
              // hier bisher trotzdem als Kritisch, obwohl die Dashboard-Kachel "Berichte nach
              // Status" genau diesen Status schon ausnimmt (Kundenfeedback 16.10.2026: beide
              // Stellen zeigten für denselben Bericht unterschiedliche Kritisch-Zahlen).
              const kritisch = activeReports.filter(r =>
                r.statusColor !== 'green' && r.status !== STATUS_BEI_BAFA && r.hasRealEndDate &&
                Math.ceil((new Date(r.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) <= 28
              ).length;
              return (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 md:mb-8">
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 md:p-6">
                    <div className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 sm:mb-2">Neu</div>
                    <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-600 dark:text-blue-500">{activeReports.filter(r => r.statusColor === 'blue').length}</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 md:p-6">
                    <div className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 sm:mb-2">In Bearbeitung</div>
                    <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-yellow-600 dark:text-yellow-500">{activeReports.filter(r => r.statusColor === 'yellow').length}</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 md:p-6">
                    <div className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 sm:mb-2">Abgeschlossen</div>
                    <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-green-600 dark:text-green-500">{activeReports.filter(r => r.statusColor === 'green').length}</div>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 md:p-6">
                    <div className="text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 mb-1 sm:mb-2">Kritisch</div>
                    <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-red-600 dark:text-red-500">{kritisch}</div>
                  </div>
                </div>
              );
            })()}

            {/* Reports Table */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
              {/* Card list */}
              {displayedReports.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
                  Keine Berichte gefunden.
                </div>
              ) : (
                <>
                {/* Mobile-Kartenliste (< md) — Tabelle ist auf kleinen Screens unbrauchbar.
                    Nutzt dasselbe openMenuAt-Portal-Menü wie die Tabelle. */}
                <ul className="md:hidden divide-y divide-slate-200 dark:divide-slate-700 border-t border-slate-200 dark:border-slate-700">
                  {displayedReports.map((report) => {
                    const remainingTime = getRemainingTimeBadge(report.endDate, report.status);
                    const displayProgress = report.statusColor === 'green' ? 100 : report.fortschritt;
                    return (
                      <li
                        key={report.id}
                        onClick={() => navigate(`/editor/${report.id}`)}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer"
                      >
                        <div className="shrink-0"><CircularProgress value={displayProgress} color={report.statusColor} /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white truncate">{report.berichtstitel}</h3>
                            {report.isShared && report.creatorName && (
                              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 shrink-0">
                                <Users className="w-3 h-3" />{report.creatorName}
                              </span>
                            )}
                          </div>
                          {report.kunde && <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{report.kunde}</p>}
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <span title={report.status} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium ${
                              report.statusColor === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                              report.statusColor === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                              'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                            }`}>{report.status}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[13px] font-medium ${remainingBadgeClasses(remainingTime.color)}`}>{remainingTime.text}</span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[13px] text-slate-400 dark:text-slate-500">
                            {report.aktenzeichen && <span className="truncate max-w-full">Az: {report.aktenzeichen}</span>}
                            <span className="whitespace-nowrap">{formatDate(report.startDate)} – {formatDate(report.endDate)}</span>
                            {(report.consultingStart || report.totalConsultingHours > 0) && (
                              <span className={`inline-flex items-center gap-1 whitespace-nowrap ${report.hasDayConflict ? 'px-1.5 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' : ''}`}>
                                {report.hasDayConflict ? <AlertTriangle className="w-3 h-3 shrink-0" /> : '⏱'} {renderZeitangaben(report)}
                              </span>
                            )}
                            {report.zuletztDatum && <span className="whitespace-nowrap">zuletzt {report.zuletztDatum}{report.zuletztZeit ? ` ${report.zuletztZeit}` : ''}</span>}
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); openMenuAt(report.id, e); }}
                          aria-label="Aktionen"
                          className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {/* Desktop-Tabelle (≥ md) — bleibt gemountet (hidden), damit das Portal-Menü auch aus den Karten funktioniert */}
                <ul
                  style={{ gridTemplateColumns: gridCols }}
                  className="hidden md:grid gap-x-3 sm:gap-x-4 divide-y divide-slate-200 dark:divide-slate-700 rounded-t-xl overflow-hidden"
                >
                  {/* Spalten-Header — folgt columnOrder, identische Breiten via subgrid */}
                  <li className="col-span-full grid grid-cols-subgrid items-center px-3 sm:px-4 py-2.5 bg-white dark:bg-slate-800 text-[13px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 select-none">
                    {columnOrder.filter(k => isOrderColVisible(k)).map(key => {
                      const align: Record<string, string> = {
                        fortschritt: 'text-center',
                        vorgangsnummer: 'text-center',
                        titel: 'text-left',
                        notiz: 'text-left',
                        zeitraum: 'text-center',
                        zeitangaben: 'text-center',
                        restzeit: 'text-center',
                        status: 'text-center',
                        zuletzt: 'text-center',
                      };
                      let label = orderLabels[key];
                      if (key === 'titel') {
                        label = visibleColumns.berichtstitel && visibleColumns.kunde ? 'Bericht / Kunde'
                          : visibleColumns.berichtstitel ? 'Bericht' : 'Kunde';
                      }
                      // Sortiert wird hier, wo die Spalte steht. Die kombinierte Titel-Spalte
                      // sortiert nach dem, was sie gerade zeigt (Bericht oder Kunde).
                      const sortKey = key === 'titel'
                        ? (visibleColumns.berichtstitel ? 'berichtstitel' : 'kunde')
                        : key === 'fortschritt' ? null
                        : key === 'notiz' ? null
                        : key;
                      if (!sortKey) return <span key={key} className={align[key]}>{label}</span>;
                      const aktiv = sortColumn === sortKey;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSort(sortKey)}
                          title={`Nach ${label} sortieren`}
                          className={`flex items-center gap-1 min-w-0 uppercase tracking-wider font-semibold text-[13px] cursor-pointer transition-colors ${align[key] === 'text-center' ? 'justify-center' : 'text-left'} ${
                            aktiv ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                          }`}
                        >
                          <span className="truncate">{label}</span>
                          <span className="shrink-0 flex flex-col leading-none -space-y-1" aria-hidden="true">
                            <ArrowUp className={`w-2.5 h-2.5 ${aktiv && sortDirection === 'asc' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-300 dark:text-slate-600'}`} />
                            <ArrowDown className={`w-2.5 h-2.5 ${aktiv && sortDirection === 'desc' ? 'text-blue-600 dark:text-blue-400' : 'text-slate-300 dark:text-slate-600'}`} />
                          </span>
                        </button>
                      );
                    })}
                    <span aria-hidden="true" />
                  </li>
                  {displayedReports.map((report) => {
                    const remainingTime = getRemainingTimeBadge(report.endDate, report.status);
                    const displayProgress = report.statusColor === 'green' ? 100 : report.fortschritt;
                    return (
                      <li
                        key={report.id}
                        onClick={() => navigate(`/editor/${report.id}`)}
                        className="col-span-full grid grid-cols-subgrid items-center px-3 sm:px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer last:rounded-b-xl"
                      >
                        {columnOrder.filter(k => isOrderColVisible(k)).map(key => {
                          switch (key) {
                            case 'fortschritt':
                              return <div key={key} className="justify-self-center"><CircularProgress value={displayProgress} color={report.statusColor} /></div>;
                            case 'vorgangsnummer':
                              return (
                                <div key={key} className="min-w-0 text-center">
                                  <span className="text-sm font-semibold text-slate-900 dark:text-white truncate block" title={report.aktenzeichen}>
                                    {report.aktenzeichen}
                                  </span>
                                </div>
                              );
                            case 'titel':
                              return (
                                <div key={key} className="min-w-0 text-left">
                                  <div className="flex items-center gap-2 min-w-0">
                                    {visibleColumns.berichtstitel && (
                                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white truncate">{report.berichtstitel}</h3>
                                    )}
                                    {report.isShared && report.creatorName && (
                                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 shrink-0">
                                        <Users className="w-3 h-3" />
                                        {report.creatorName}
                                      </span>
                                    )}
                                  </div>
                                  {visibleColumns.kunde && (
                                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">{report.kunde}</p>
                                  )}
                                </div>
                              );
                            case 'notiz':
                              return (
                                <div key={key} className="min-w-0 text-left">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setNotizEdit({ id: report.id, titel: report.berichtstitel, value: report.notiz ?? '' }); }}
                                    title={report.notiz || 'Notiz hinzufügen'}
                                    className="w-full text-left text-xs leading-tight px-1.5 py-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                  >
                                    {report.notiz
                                      ? <span className="text-slate-600 dark:text-slate-300 line-clamp-2 whitespace-pre-wrap break-words">{report.notiz}</span>
                                      : <span className="inline-flex items-center gap-1 text-slate-300 dark:text-slate-600"><StickyNote className="w-3.5 h-3.5" /> Notiz…</span>}
                                  </button>
                                </div>
                              );
                            case 'zeitraum':
                              return (
                                <div key={key} className="text-center">
                                  <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                    {formatDate(report.startDate)} – {formatDate(report.endDate)}
                                  </span>
                                </div>
                              );
                            case 'zeitangaben':
                              return (
                                <div key={key} className="flex justify-center">
                                  <span
                                    title={report.hasDayConflict ? 'Terminüberschneidung mit anderem Bericht im Team' : undefined}
                                    className={`inline-flex items-center gap-1 text-xs leading-tight text-center ${
                                      report.hasDayConflict
                                        ? 'px-2 py-0.5 rounded-full font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                                        : 'text-slate-500 dark:text-slate-400'
                                    }`}
                                  >
                                    {report.hasDayConflict && <AlertTriangle className="w-3 h-3 shrink-0" />}
                                    {renderZeitangaben(report)}
                                  </span>
                                </div>
                              );
                            case 'restzeit':
                              return (
                                <div key={key} className="flex justify-center">
                                  <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[13px] font-medium w-full whitespace-nowrap ${remainingBadgeClasses(remainingTime.color)}`}>
                                    {remainingTime.text}
                                  </span>
                                </div>
                              );
                            case 'status':
                              return (
                                <div key={key} className="flex justify-center">
                                  <span title={report.status} className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[13px] font-medium w-full truncate ${
                                    report.statusColor === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                    report.statusColor === 'green' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                    'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                                  }`}>
                                    {report.status}
                                  </span>
                                </div>
                              );
                            case 'zuletzt':
                              return (
                                <div key={key} className="text-center">
                                  <div className="text-xs text-slate-500 dark:text-slate-400 leading-tight whitespace-nowrap">{report.zuletztDatum}</div>
                                  {report.zuletztZeit && (
                                    <div className="text-[13px] text-slate-400 dark:text-slate-500 leading-tight whitespace-nowrap">{report.zuletztZeit} Uhr</div>
                                  )}
                                </div>
                              );
                            default:
                              return null;
                          }
                        })}
                        {/* Actions */}
                        <div className="relative shrink-0 ml-auto">
                          <button
                            onClick={(e) => { e.stopPropagation(); openMenuAt(report.id, e); }}
                            onMouseLeave={scheduleMenuClose}
                            onMouseEnter={cancelMenuClose}
                            aria-label="Aktionen"
                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-white"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {openMenuId === report.id && menuPos && createPortal(
                            <>
                              <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); }} />
                              <div
                                style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
                                onMouseEnter={cancelMenuClose}
                                onMouseLeave={scheduleMenuClose}
                                className="w-48 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 py-1 whitespace-nowrap"
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    navigate(`/editor/${report.id}`);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                  Bearbeiten
                                </button>
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    setMenuBtnEl(null);
                                    const next = !report.archived;
                                    try {
                                      await api.patch(`/api/reports/${report.id}/archive`, { archived: next });
                                      setAllReports(prev => prev.map(r => r.id === report.id ? { ...r, archived: next } : r));
                                    } catch { /* noop */ }
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                                >
                                  {report.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                                  {report.archived ? 'Wiederherstellen' : 'Archivieren'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenMenuId(null);
                                    setDeleteConfirm({ id: report.id, title: report.berichtstitel });
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-slate-600 transition-colors"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  Löschen
                                </button>
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                </>
              )}

              {/* Fußzeile: Trefferzahl links, Kontingent und Seitengröße rechts. */}
              <div className="px-3 sm:px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  {filteredReports.length} {filteredReports.length === 1 ? 'Bericht' : 'Berichte'} gefunden
                  {filteredReports.length > rowsPerPage && `, ${rowsPerPage} angezeigt`}
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  {status && (
                    <span
                      title={`${status.reportUsage.used} von ${status.reportUsage.limit} ${status.reportUsage.mode === 'monthly' ? 'Berichten diesen Monat' : 'Berichten insgesamt'}`}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium tabular-nums ${
                        isAtReportLimit
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {status.reportUsage.used}/{status.reportUsage.limit}
                    </span>
                  )}
                  <div className="relative">
                    <button
                      onClick={() => { setRowsDropdownOpen(!rowsDropdownOpen); setColumnsDropdownOpen(false); setStatusDropdownOpen(false); }}
                      title="Einträge pro Seite"
                      className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center gap-1 cursor-pointer"
                    >
                      <span>{rowsPerPage} pro Seite</span>
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    </button>
                    {rowsDropdownOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setRowsDropdownOpen(false)} />
                        <div className="absolute right-0 bottom-full mb-2 min-w-[120px] bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-20">
                          <div className="p-1">
                            {[10, 25, 50].map((n) => (
                              <button key={n} onClick={() => { setRowsPerPage(n); setRowsDropdownOpen(false); }}
                                className={`w-full px-4 py-2 text-sm rounded text-center cursor-pointer ${n === rowsPerPage ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'}`}
                              >{n}</button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Filter-Dialog (nur Mobil) — Vollbild, Suche offen, der Rest als Ziehharmonika.
          Die Seitengröße bleibt bewusst draußen in der Fußzeile der Liste. */}
      {filterSheetOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-white dark:bg-slate-800">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Filter</h3>
            <button onClick={() => setFilterSheetOpen(false)} aria-label="Schließen"
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Kunde, Titel oder Vorgangsnummer..."
                  aria-label="Berichte durchsuchen"
                  className="w-full h-11 pl-9 pr-9 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchQuery !== '' && (
                  <button onClick={() => setSearchQuery('')} aria-label="Suche zurücksetzen"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {([
              { key: 'sortierung' as const, label: 'Sortierung' },
              { key: 'status' as const, label: 'Status' },
              { key: 'spalten' as const, label: 'Spalten' },
            ]).map(bereich => {
              const offen = offenerBereich === bereich.key;
              return (
                <div key={bereich.key} className="border-b border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => setOffenerBereich(offen ? null : bereich.key)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-slate-900 dark:text-white"
                  >
                    <span className="flex items-center gap-2">
                      {bereich.label}
                      {bereich.key === 'sortierung' && (
                        <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                          {SORT_OPTIONS.find(o => o.key === sortColumn)?.label}
                        </span>
                      )}
                      {bereich.key === 'status' && statusFilterActive && (
                        <span className="text-xs font-normal text-blue-600 dark:text-blue-400">{checkedStatusCount} ausgewählt</span>
                      )}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${offen ? '' : '-rotate-90'}`} />
                  </button>

                  {offen && bereich.key === 'sortierung' && (
                    <div className="px-2 pb-3">
                      {SORT_OPTIONS.map(o => {
                        const aktiv = o.key === sortColumn;
                        return (
                          <button
                            key={o.key}
                            onClick={() => setSort(o.key)}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded text-sm text-left ${
                              aktiv ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            <span>{o.label}</span>
                            {aktiv && (sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />)}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {offen && bereich.key === 'status' && (
                    <div className="px-2 pb-3">
                      <label className="flex items-center gap-2 px-3 py-2.5 rounded">
                        <input type="checkbox" checked={allStatusChecked} onChange={toggleAllStatus} className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-900 dark:text-white">Alle Status</span>
                      </label>
                      {statusOptions.map(st => (
                        <label key={st} className="flex items-center gap-2 px-3 py-2 rounded">
                          <input type="checkbox" checked={isStatusChecked(st)} onChange={() => toggleStatus(st)} className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{st}</span>
                        </label>
                      ))}
                      <label className="flex items-center gap-2 px-3 py-2 rounded border-t border-slate-200 dark:border-slate-700 mt-1 pt-2">
                        <input type="checkbox" checked={showArchivedStatus} onChange={() => setShowArchivedStatus(v => !v)} className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">Archiviert</span>
                      </label>
                    </div>
                  )}

                  {offen && bereich.key === 'spalten' && (
                    <div className="px-2 pb-3">
                      <label className="flex items-center gap-2 px-3 py-2.5 rounded">
                        <input type="checkbox" checked={allColumnsVisible} onChange={toggleAllColumns} className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-slate-900 dark:text-white">Alle Spalten</span>
                      </label>
                      {columnOrder.map(col => (
                        <label key={col} className="flex items-center gap-2 px-3 py-2 rounded">
                          <input
                            type="checkbox"
                            checked={isOrderColVisible(col)}
                            onChange={() => {
                              if (col === 'titel') {
                                const next = !(visibleColumns.berichtstitel || visibleColumns.kunde);
                                setVisibleColumns(p => ({ ...p, berichtstitel: next, kunde: next }));
                              } else {
                                toggleColumn(col);
                              }
                            }}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{orderLabels[col]}</span>
                        </label>
                      ))}
                      <div className="ml-6 border-l border-slate-200 dark:border-slate-600 pl-2">
                        <label className="flex items-center gap-2 px-2 py-1.5 rounded">
                          <input type="checkbox" checked={!!visibleColumns.berichtstitel} onChange={() => toggleColumn('berichtstitel')} className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-slate-600 dark:text-slate-400">Berichtstitel</span>
                        </label>
                        <label className="flex items-center gap-2 px-2 py-1.5 rounded">
                          <input type="checkbox" checked={!!visibleColumns.kunde} onChange={() => toggleColumn('kunde')} className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-slate-600 dark:text-slate-400">Kunde</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 shrink-0">
            <button
              onClick={() => setFilterSheetOpen(false)}
              className="w-full px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
            >
              {filteredReports.length} {filteredReports.length === 1 ? 'Bericht' : 'Berichte'} anzeigen
            </button>
          </div>
        </div>
      )}

      <PlanLimitModal
        open={limitModalCode !== null}
        code={limitModalCode}
        onClose={() => setLimitModalCode(null)}
      />
      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Bericht löschen"
        message={deleteConfirm ? `Bericht "${deleteConfirm.title}" wirklich löschen?` : ''}
        confirmLabel="Löschen"
        onConfirm={performDeleteReport}
        onCancel={() => setDeleteConfirm(null)}
      />
      {notizEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="rounded-xl border shadow-xl w-full max-w-md bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white truncate">Notiz — {notizEdit.titel}</h3>
            </div>
            <div className="px-5 py-4">
              <textarea
                rows={6}
                value={notizEdit.value}
                onChange={(e) => setNotizEdit({ ...notizEdit, value: e.target.value })}
                placeholder="Notiz eingeben…"
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none"
              />
            </div>
            <div className="px-5 py-3 flex justify-end gap-2 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setNotizEdit(null)}
                disabled={notizSaving}
                className="px-4 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Abbrechen
              </button>
              <button
                onClick={saveNotiz}
                disabled={notizSaving}
                className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
              >
                {notizSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
